import { FontCategory, FontListItemInterface } from '../../../types/FontInterface';
import {
  RestaurantColorSettingsInterface,
  RestaurantFontSettingsInterface
} from '../../../types/RestaurantTypographyInterface';
import { ColorCategoryId, DEFAULT_COLOR, FontCategoryId, FontRow } from './designPageTypes';

export function isValidFontPayload(payload: RestaurantFontSettingsInterface): boolean {
  return Object.values(payload).every((slot) => slot.fontFamily?.trim() && /^#[0-9A-F]{6}$/i.test(slot.color));
}

export function isValidColorPayload(payload: RestaurantColorSettingsInterface): boolean {
  return Object.values(payload).every((slot) => /^#[0-9A-F]{6}$/i.test(slot.color));
}

export function normalizeHex(input: string): string | null {
  let hex = input.trim();
  if (!hex) {
    return null;
  }
  if (!hex.startsWith('#')) {
    hex = `#${hex}`;
  }
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    const [, short] = hex.match(/^#([0-9a-fA-F]{3})$/) || [];
    const r = short[0];
    const g = short[1];
    const b = short[2];
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  if (/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return hex.toUpperCase();
  }
  return null;
}

export function resolveFontFamily(stored: string, choices: FontListItemInterface[]): string {
  if (choices.length === 0) {
    return stored;
  }
  const titles = choices.map((font) => font.title);
  return titles.includes(stored) ? stored : choices[0].title;
}

export function createDefaultFontRows(defaultFamily: string): Record<FontCategoryId, FontRow> {
  return {
    navigation: { family: defaultFamily, color: DEFAULT_COLOR },
    body: { family: defaultFamily, color: DEFAULT_COLOR },
    pageHeaders: { family: defaultFamily, color: DEFAULT_COLOR },
    subheaders: { family: defaultFamily, color: DEFAULT_COLOR },
    menus: { family: defaultFamily, color: DEFAULT_COLOR },
    menuSections: { family: defaultFamily, color: DEFAULT_COLOR },
    menuItems: { family: defaultFamily, color: DEFAULT_COLOR }
  };
}

export function createDefaultColorRows(): Record<ColorCategoryId, string> {
  return {
    navigationBackground: DEFAULT_COLOR,
    websiteBackground: DEFAULT_COLOR,
    onlineOrderingBackground: DEFAULT_COLOR,
    onlineOrderingActiveSelections: DEFAULT_COLOR
  };
}

export function toApiFontSettings(rows: Record<FontCategoryId, FontRow>): RestaurantFontSettingsInterface {
  const slot = (id: FontCategoryId) => ({
    fontFamily: rows[id].family,
    color: normalizeHex(rows[id].color) ?? rows[id].color
  });

  return {
    navigation: slot('navigation'),
    body: slot('body'),
    pageHeaders: slot('pageHeaders'),
    subheaders: slot('subheaders'),
    menus: slot('menus'),
    menuSections: slot('menuSections'),
    menuItems: slot('menuItems')
  };
}

export function fromApiFontSettings(
  api: RestaurantFontSettingsInterface,
  choices: FontListItemInterface[]
): Record<FontCategoryId, FontRow> {
  const defaultFamily = choices[0]?.title ?? '';
  const mapSlot = (slot: { fontFamily: string; color: string }) => ({
    family: resolveFontFamily(slot.fontFamily, choices) || defaultFamily,
    color: normalizeHex(slot.color) || slot.color || DEFAULT_COLOR
  });

  return {
    navigation: mapSlot(api.navigation),
    body: mapSlot(api.body),
    pageHeaders: mapSlot(api.pageHeaders),
    subheaders: mapSlot(api.subheaders),
    menus: mapSlot(api.menus),
    menuSections: mapSlot(api.menuSections),
    menuItems: mapSlot(api.menuItems)
  };
}

export function toApiColorSettings(rows: Record<ColorCategoryId, string>): RestaurantColorSettingsInterface {
  const slot = (id: ColorCategoryId) => ({
    color: normalizeHex(rows[id]) ?? rows[id]
  });

  return {
    navigationBackground: slot('navigationBackground'),
    websiteBackground: slot('websiteBackground'),
    onlineOrderingBackground: slot('onlineOrderingBackground'),
    onlineOrderingActiveSelections: slot('onlineOrderingActiveSelections')
  };
}

export function fromApiColorSettings(
  api: RestaurantColorSettingsInterface | undefined
): Record<ColorCategoryId, string> {
  const defaults = createDefaultColorRows();
  const mapSlot = (slot: { color: string } | undefined, fallback: string) => {
    if (!slot?.color) {
      return fallback;
    }
    return normalizeHex(slot.color) || slot.color || fallback;
  };

  return {
    navigationBackground: mapSlot(api?.navigationBackground, defaults.navigationBackground),
    websiteBackground: mapSlot(api?.websiteBackground, defaults.websiteBackground),
    onlineOrderingBackground: mapSlot(api?.onlineOrderingBackground, defaults.onlineOrderingBackground),
    onlineOrderingActiveSelections: mapSlot(
      api?.onlineOrderingActiveSelections,
      defaults.onlineOrderingActiveSelections
    )
  };
}

const FONT_GROUPS: { category: FontCategory; label: string }[] = [
  { category: 'sans', label: 'Sans Serif' },
  { category: 'serif', label: 'Serif' },
  { category: 'display_serif', label: 'Display Serif' },
  { category: 'display_accent', label: 'Display & Script' },
  { category: 'platform_default', label: 'TapTab Default' }
];

export function groupFontChoices(choices: FontListItemInterface[]): { label: string; fonts: FontListItemInterface[] }[] {
  const known = new Set(FONT_GROUPS.map((group) => group.category));
  return [
    ...FONT_GROUPS.map(({ category, label }) => ({
      label,
      fonts: choices.filter((font) => font.category === category)
    })),
    { label: 'Other', fonts: choices.filter((font) => !known.has(font.category)) }
  ].filter((group) => group.fonts.length > 0);
}

